import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from '@carecloud/cloudpak';
import { connect } from 'react-redux';
import { store } from '../../../models';
import { Modal } from './modal';
import { ContextProvider } from '../../root/TopLevelContext';

// Wizard-style modals (see ModalParser.generateSignInWithCodeModal in
// cyclops) bake every step into the schema at generation time — the schema
// is fetched once and cached in Redux, so there's no way to re-invoke cyclops
// client-side to get a fresh step. Instead, each step lives in its own
// `${modalId}Step<N>` group and the matching stepper is `${modalId}Stepper`;
// this walks the (small, already-parsed) content tree on render and prunes
// whichever step group isn't active, patching the stepper to match. No-op
// for every other modal, since their ids never match this naming pattern.
const applyWizardStep = (node, modalId, currentStep) => {
  if (!node || typeof node !== 'object') return node;
  if (node.id === `${modalId}Stepper`) {
    // Hide the stepper on the final step (e.g. the "You're signed in"
    // success screen) — there's nothing left to track progress toward.
    if (node.steps && currentStep >= node.steps) return null;
    return {
      ...node,
      currentStep,
      progress: node.steps ? Math.round((currentStep / node.steps) * 100) : node.progress,
    };
  }
  const stepMatch = typeof node.id === 'string' && node.id.match(new RegExp(`^${modalId}Step(\\d+)$`));
  if (stepMatch && Number(stepMatch[1]) !== currentStep) return null;
  if (Array.isArray(node.components)) {
    return { ...node, components: node.components.map(child => applyWizardStep(child, modalId, currentStep)) };
  }
  return node;
};

class ModalContainer extends PureComponent {
  static contextType = ContextProvider;
  constructor(props) {
    super(props);
    this.state = {
      errorMessage: null,
      otpError: false,
    };
  }
  componentDidMount() {
    const parsedLocalActions = this.props.localActions && JSON.parse(this.props.localActions);
    this.context._setActions({ ...parsedLocalActions });
    if (this.props.externalData) {
      store.dispatch.form.addExternalSubmissionData(this.props.externalData);
    }
  }
  componentWillReceiveProps({ modal: { cancel } }) {
    const {
      modal: { modalOpen },
    } = this.props;
    if (cancel && modalOpen) {
      store.dispatch.modal.toggleCancel(false);
      this.clickHandler();
    }
  }
  componentDidUpdate(e){
    const { error } = this.props;
    // clearing error on modal close
    if (e.modal.transform === false)
      this.setState({ errorMessage: null}) //eslint-disable-line react/no-did-update-set-state
    if (error?.errors) {
      this.setState({ errorMessage: error.errors[0].message }); //eslint-disable-line react/no-did-update-set-state
      store.dispatch.error.resetError();
    }
  }
  componentWillUnmount() {
    store.dispatch.modal.toggleOpen(false);
    document.body.classList.remove('modal-open');
  }
  clickHandler = _ => {
    // Closing a modal while one of its buttons left a request "in flight"
    // (showLoader: true, e.g. sendSignInCode/verifySignInCode) shouldn't
    // leave the page stuck under Layout.jsx's full-screen loader — that
    // request can't be cancelled, but the user closing the modal means
    // they've abandoned it, so nothing should still be blocking clicks
    // app-wide once they have.
    store.dispatch.loader.persistLoader({ persist: false });
    const { radioInputCancellationReason } = this.props;
    const {
      radioInput: { resetRadioInput } = {},
      formGroup: { resetEvents } = {},
      inputDropdown: { resetValues } = {},
      modal: { toggleOpen, toggleTransform } = {},
    } = store.dispatch;
    setTimeout(() => {
      toggleOpen(null);
      toggleTransform(true);
      radioInputCancellationReason && resetRadioInput() && resetEvents();
      store.getState().inputDropdown.createVisitSummaryPractice && resetValues();
    }, 300);
    toggleTransform(false);
  };
  handleOutsideClick = e => {
    const { hideClose, closeOnOutsideClick = false } = this.props;
    if (closeOnOutsideClick) {
      store.dispatch.loader.persistLoader({ persist: false });
      store.dispatch.modal.toggleClose();
    }
    if (e.target.id === 'outerContent' && !hideClose) {
      this.clickHandler();
    }
  };
  render() {
    const {
      modal: { transform, modalOpen },
    } = this.props;
    modalOpen ? document.body.classList.add('modal-open') : document.body.classList.remove('modal-open');
    // disable select business continue button on opening Modal
    if (this.props.id === 'selectBusiness'){
      store.dispatch.button.enableButton({
        btn: 'intelligentSchedulerContinue',
        complete: false,
      });
    }
    if (this.props.id === 'signInOTPModal'){
      this.setState(prevState => {
        return {
          ...prevState,
          otpError: true,
        };
      });
    }

    const currentStep = (this.props.modalSteps && this.props.modalSteps[this.props.id]) || 1;
    const content = this.props.content ? applyWizardStep(this.props.content, this.props.id, currentStep) : this.props.content;

    return (
      <Modal
        {...{
          ...this.props,
          transform,
          modalOpen,
          content,
          otpError: this.state.otpError,
          errorMessage: this.state.errorMessage,
          handleOutsideClick: this.handleOutsideClick,
          clickHandler: this.clickHandler,
        }}
      />
    );
  }
}
const mapStateToProps = ({ error, login }) => ({
  error,
  modalSteps: login.modalSteps,
});
const enhancer = compose(
  withRouter,
  connect(mapStateToProps),
);
export default enhancer(ModalContainer);
